import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_05 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_VALUE_ZERO,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import {
  MeasuringUnitMessages,
  UITextMessages,
  getFieldMaxLengthMessage
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface UomItem {
  measuringunitcode?: string | number;
  measuringunit?: string;
  descrip?: string;
}

interface UomDetails {
  immeasuringunit: string;
  descrip: string;
  immeasuringunitcode: string | number;
}

interface ErrorState {
  immeasuringunit?: string;
  descrip?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  RESTCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: UomItem;
  actionText?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreateUom(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const location = useLocation();

  const { selectedItem } = (location.state as LocationState) || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { STAFFMAPLOCODE, USERNAME, COMPCODE } = SYS_USER.USER_PARAM || {};

  const [data, setData] = useState<UomItem[]>([]);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [selectedItemDetails, setSelectedItemDetails] = useState<UomDetails>({
    immeasuringunit: "",
    descrip: "",
    immeasuringunitcode: CON_VALUE_ZERO,
  });

  /* ================= EDIT MODE ================= */
  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        immeasuringunit: selectedItem.measuringunit || "",
        descrip: selectedItem.descrip || "",
        immeasuringunitcode: selectedItem.measuringunitcode || CON_VALUE_ZERO,
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    setPageData({ title: UITextMessages.breadcrumb.measuringUnit } as PageData);
  }, [setPageData]);

  /* ================= FETCH EXISTING UOMs ================= */
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_05, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });
        setData((result as UomItem[]) || []);
      } catch (err) {
        console.error(err);
        setAlertType("error");
        setAlertMessage(MeasuringUnitMessages.error.fetchFailed);
        setAlertOpen(true);
      }
    };
    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  /* ================= INPUT HANDLERS ================= */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const noLeadingSpaceValue = value.replace(/^\s+/, "");
    let errorMsg = "";

    if (noLeadingSpaceValue.length === CON_MAX_NAME_LENGTH) {
      errorMsg = getFieldMaxLengthMessage(UITextMessages.label.measuringUnit, CON_MAX_NAME_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  const handleInputChangedes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const noLeadingSpaceValue = value.replace(/^\s+/, "");
    let errorMsg = "";

    if (noLeadingSpaceValue.length === CON_MAX_DESCRIPTION_LENGTH) {
      errorMsg = getFieldMaxLengthMessage(UITextMessages.label.description, CON_MAX_DESCRIPTION_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  /* ================= ACTIONS ================= */
  const handleCancel = (): void => {
    // Clear any error messages and navigate back to the previous page
    setError({});
    navigate("/uom");
  };

  const goBackToView = (): void => {
    navigate("/uom");
  };

  /* ================= SAVE ================= */
  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);

    const { immeasuringunit, descrip, immeasuringunitcode } = selectedItemDetails;

    let validationErrors: ErrorState = {};
    let hasError = false;

    if (!immeasuringunit.trim()) {
      validationErrors.immeasuringunit = MeasuringUnitMessages.warning.nameRequired;
      hasError = true;
    }

    setError(validationErrors);

    if (hasError) {
      setAlertType("error");
      setAlertMessage(MeasuringUnitMessages.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    /* ===== DUPLICATE CHECK ===== */
    const isDuplicate = data?.some(
      (item) =>
        item.measuringunit?.toLowerCase() === immeasuringunit.toLowerCase() &&
        item.measuringunitcode !== immeasuringunitcode
    );

    if (isDuplicate) {
      setAlertType("warning");
      setAlertMessage(MeasuringUnitMessages.warning.alreadyExists);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    const payload = {
      measuringunitcode: immeasuringunitcode,
      measuringunit: immeasuringunit,
      descrip,
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    try {
      await externalpostApi(SETTING_API_05, payload);
      setAlertType("success");
      setAlertMessage(isEdit ? MeasuringUnitMessages.success.update : MeasuringUnitMessages.success.create);
      setAlertOpen(true);
      setTimeout(() => navigate("/uom"), 2000);
    } catch (err) {
      const error = err as ApiError;
      console.error("API Error:", error);
      setAlertType("error");
      setAlertMessage(error.response?.data?.message || MeasuringUnitMessages.error.unexpected);
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title={UITextMessages.tooltip.back} arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon} />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs separator="›" aria-label="breadcrumb" sx={styles.breadcrumb}>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link_page} aria-current="page">
            {UITextMessages.breadcrumb.measuringUnit}
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid size={{ xs: 8, sm: 10 }} sx={styles.appbar_grid_container_col_1}>
              <Typography sx={styles.appbar_title}>
                {isEdit ? UITextMessages.pageTitle.edit : UITextMessages.pageTitle.create}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.add_form_page_container}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_measuringunit"
                label={UITextMessages.label.measuringUnit}
                fullWidth
                size="small"
                required
                name="immeasuringunit"
                value={selectedItemDetails.immeasuringunit}
                onChange={handleInputChange}
                error={!!error.immeasuringunit}
                helperText={error.immeasuringunit}
                inputProps={{ maxLength: CON_MAX_NAME_LENGTH }}
                FormHelperTextProps={{ sx: { fontSize: 12, color: "red" } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} />

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_description"
                label={UITextMessages.label.description}
                fullWidth
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChangedes}
                error={!!error.descrip}
                helperText={error.descrip}
                inputProps={{ maxLength: CON_MAX_DESCRIPTION_LENGTH }}
                FormHelperTextProps={{ sx: { fontSize: 12, color: "red" } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_cancel"
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            {UITextMessages.button.cancel || "Cancel"}
          </Button>

          <Button
            id="btn_confirm"
            variant="contained"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            disabled={isButtonDisabled}
            onClick={handleConfirmClick}
          >
            {isEdit ? UITextMessages.button.modify : UITextMessages.button.confirm}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateUom;